import { Button, Group, Image, Modal, Stack, Text, ThemeIcon } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { AddSquareGlyph, ChipButton, ShareGlyph } from '@/components';
import { usePwaInstallPrompt } from '@/hooks';

/**
 * Modal nudge to install the app: a deliberately high-visibility takeover
 * (not a quiet banner) shown once per browser session — either a native
 * "Install" flow on Android/Chrome (via `beforeinstallprompt`), or manual
 * "Add to Home Screen" steps on iOS Safari, which has no such event.
 */
export const PwaInstallBanner = () => {
  const { t } = useTranslation();
  const { shouldShowInstallModal, canPromptInstall, promptInstall, dismiss } =
    usePwaInstallPrompt();

  return (
    <Modal
      centered
      onClose={dismiss}
      opened={shouldShowInstallModal}
      padding="lg"
      title={
        <Text c="ink.9" ff="heading" fw={800} size="lg" tt="uppercase">
          {t('pwaInstall.title')}
        </Text>
      }
      withCloseButton={false}
    >
      <Stack gap="lg">
        <Group gap="md" wrap="nowrap">
          <Image alt="" h={56} radius={0} src="/pwa-192x192.png" w={56} />
          <Text c="ink.7" size="sm">
            {t('pwaInstall.body')}
          </Text>
        </Group>

        {!canPromptInstall && (
          <Stack gap="sm">
            <Group gap="sm" wrap="nowrap">
              <ThemeIcon color="signal" radius={0} size="lg" variant="light">
                <ShareGlyph />
              </ThemeIcon>
              <Text c="ink.8" ff="monospace" size="sm">
                {t('pwaInstall.iosStep1')}
              </Text>
            </Group>
            <Group gap="sm" wrap="nowrap">
              <ThemeIcon color="signal" radius={0} size="lg" variant="light">
                <AddSquareGlyph />
              </ThemeIcon>
              <Text c="ink.8" ff="monospace" size="sm">
                {t('pwaInstall.iosStep2')}
              </Text>
            </Group>
          </Stack>
        )}

        <Group grow wrap="nowrap">
          <ChipButton active={false} fullWidth label={t('pwaInstall.notNow')} onClick={dismiss} />
          {canPromptInstall && (
            <Button onClick={promptInstall} style={{ boxShadow: 'none' }}>
              {t('pwaInstall.install')}
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
};
